interface Point2D {
    x: number;
    y: number;
}

interface BuildArcMotionFramesParams {
    start: Point2D;
    end: Point2D;
    startScale: number;
    midScale: number;
    endScale: number;
    arcHeight: number;
    rotateDeg: number;
}

export const clamp = (value: number, min: number, max: number): number => {
    return Math.min(Math.max(value, min), max);
};

export const lerp = (a: number, b: number, t: number): number => {
    return a + (b - a) * t;
};

export const easeOutCubic = (t: number): number => {
    return 1 - Math.pow(1 - t, 3);
};

export const quadraticBezierPoint = (p0: Point2D, p1: Point2D, p2: Point2D, t: number): Point2D => {
    const mt = 1 - t;

    return {
        x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
        y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
    };
};

export const buildArcMotionFrames = ({
    start,
    end,
    startScale,
    midScale,
    endScale,
    arcHeight,
    rotateDeg,
}: BuildArcMotionFramesParams): Keyframe[] => {
    const dx = end.x - start.x;

    const p0 = start;
    const p1 = {
        x: start.x + dx * 0.5,
        y: Math.min(start.y, end.y) - arcHeight,
    };
    const p2 = end;

    const steps = 28;
    const frames: Keyframe[] = [];

    for (let i = 0; i <= steps; i += 1) {
        const t = i / steps;
        const point = quadraticBezierPoint(p0, p1, p2, t);
        const easedT = easeOutCubic(t);

        let scale: number;

        if (easedT < 0.65) {
            scale = lerp(startScale, midScale, easedT / 0.65);
        } else {
            scale = lerp(midScale, endScale, (easedT - 0.65) / 0.35);
        }

        const rotate = t < 0.5 ? lerp(0, rotateDeg, t / 0.5) : lerp(rotateDeg, 0, (t - 0.5) / 0.5);
        const opacity = t < 0.84 ? 1 : Math.max(0.12, lerp(1, 0.12, (t - 0.84) / 0.16));

        frames.push({
            transform: `translate3d(${point.x}px, ${point.y}px, 0) scale(${scale}) rotate(${rotate}deg)`,
            opacity,
            offset: t,
        });
    }

    return frames;
};
